import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage, ClauseBuilderConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { BookOpen, RotateCcw } from 'lucide-react-native';

interface ClauseBuilderActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

const ROLE_COLORS: Record<string, { bg: string; border: string; text: string; label: string }> = {
    fronted_adverbial: {
        bg: '#F3E8FF',
        border: '#A855F7',
        text: '#7E22CE',
        label: 'Fronted Adverbial',
    },
    main_clause: {
        bg: '#EFF6FF',
        border: '#3B82F6',
        text: '#1D4ED8',
        label: 'Main Clause',
    },
    subordinate_clause: {
        bg: '#ECFDF5',
        border: '#10B981',
        text: '#047857',
        label: 'Subordinate Clause',
    },
    relative_clause: {
        bg: '#FEF3C7',
        border: '#F59E0B',
        text: '#B45309',
        label: 'Relative Clause',
    },
};

export const ClauseBuilderActivity: React.FC<ClauseBuilderActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as ClauseBuilderConfig;
    const allClauses = useMemo(() => config.clauses || [], [config.clauses]);
    const validOrders = useMemo(() => config.validOrders || [], [config.validOrders]);

    // Ordered array of selected clause IDs
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [statusMessage, setStatusMessage] = useState<string>(
        config.prompt || 'Arrange the clause blocks to create a grammatically correct sentence.'
    );
    const [hasCompleted, setHasCompleted] = useState(false);

    // Unselected clauses remaining in pool
    const unselectedClauses = useMemo(() => {
        return allClauses.filter((c) => !selectedIds.includes(c.id));
    }, [allClauses, selectedIds]);

    // Assembled sentence string with grammar formatting
    const assembledSentence = useMemo(() => {
        if (selectedIds.length === 0) return '';
        const tokens = selectedIds.map((id) => allClauses.find((c) => c.id === id)?.text || '');
        const joined = tokens.join(' ').trim();
        // Capitalize first letter
        const capitalized = joined.charAt(0).toUpperCase() + joined.slice(1);
        // Ensure trailing period
        return capitalized.endsWith('.') ? capitalized : `${capitalized}.`;
    }, [selectedIds, allClauses]);

    // Detect if fronted adverbial is placed first
    const isFrontedAdverbialFirst = useMemo(() => {
        if (selectedIds.length === 0) return false;
        const first = allClauses.find((c) => c.id === selectedIds[0]);
        return first?.role === 'fronted_adverbial';
    }, [selectedIds, allClauses]);

    const selectClause = (id: string) => {
        if (hasCompleted) return;
        setSelectedIds((prev) => [...prev, id]);
        setTrafficStatus('idle');
    };

    const deselectClause = (id: string) => {
        if (hasCompleted) return;
        setSelectedIds((prev) => prev.filter((item) => item !== id));
        setTrafficStatus('idle');
    };

    const resetSelection = () => {
        if (hasCompleted) return;
        setSelectedIds([]);
        setTrafficStatus('idle');
        setStatusMessage('Tap clause blocks to rebuild your sentence.');
    };

    const handleCheck = () => {
        if (selectedIds.length < allClauses.length || hasCompleted) return;

        // Check if selectedIds matches any valid order
        const isOrderValid = validOrders.some((order) => {
            if (order.length !== selectedIds.length) return false;
            return order.every((id, idx) => id === selectedIds[idx]);
        });

        if (isOrderValid || validOrders.length === 0) {
            setTrafficStatus('correct');
            setStatusMessage('Excellent Grammar! Your sentence is structured with perfect clause cohesion.');
            setHasCompleted(true);

            useStrokeStore.getState().addCustomAnswer({
                activityId: activity.id,
                value: assembledSentence,
                mechanic: 'clause_builder',
                isCorrect: true,
                score: 100,
                selectedOrder: selectedIds,
            });

            setTimeout(() => {
                onComplete();
            }, 1200);
        } else {
            setTrafficStatus('incorrect');
            setStatusMessage('Check clause order: Make sure the main clause and subordinate clauses link smoothly.');
        }
    };

    return (
        <StageCard testID="clause-builder-card">
            <View style={styles.container}>
                {/* Header Row */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badge}>
                            <BookOpen size={14} color="#4F46E5" />
                            <Text style={styles.badgeText}>Sentence Architecture & SPaG Lab</Text>
                        </View>
                        <Text style={styles.promptText}>{config.prompt || 'Build a cohesive sentence from clauses.'}</Text>
                    </View>
                    <View style={styles.headerControls}>
                        {assembledSentence ? (
                            <AudioPromptButton
                                promptText={assembledSentence}
                                activityId={activity.id}
                                testID="clause-builder-audio-button"
                            />
                        ) : null}
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Assembled Sentence Preview Box */}
                <View style={styles.sentenceStage}>
                    <View style={styles.stageHeader}>
                        <Text style={styles.stageTitle}>YOUR SENTENCE:</Text>
                        <TouchableOpacity
                            style={styles.resetButton}
                            onPress={resetSelection}
                            disabled={hasCompleted || selectedIds.length === 0}
                            accessibilityLabel="Reset clauses"
                            testID="clause-reset-button"
                        >
                            <RotateCcw size={14} color="#64748B" />
                            <Text style={styles.resetButtonText}>Reset</Text>
                        </TouchableOpacity>
                    </View>

                    {selectedIds.length === 0 ? (
                        <View style={styles.emptyStage}>
                            <Text style={styles.emptyStageText}>Tap clauses below to assemble your sentence.</Text>
                        </View>
                    ) : (
                        <View style={styles.stagedTokensContainer}>
                            {selectedIds.map((id, index) => {
                                const token = allClauses.find((c) => c.id === id);
                                if (!token) return null;
                                const theme = ROLE_COLORS[token.role] || ROLE_COLORS.main_clause;
                                return (
                                    <TouchableOpacity
                                        key={`staged_${id}`}
                                        style={[styles.stagedToken, { backgroundColor: theme.bg, borderColor: theme.border }]}
                                        onPress={() => deselectClause(id)}
                                        disabled={hasCompleted}
                                        accessibilityLabel={`Remove clause ${token.text}`}
                                        testID={`staged-clause-${id}`}
                                    >
                                        <Text style={[styles.stagedTokenRole, { color: theme.text }]}>
                                            {index + 1}. {theme.label}
                                        </Text>
                                        <Text style={styles.stagedTokenText}>{token.text}</Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                    )}

                    {assembledSentence ? (
                        <View style={styles.liveSentenceBox}>
                            <Text style={styles.liveSentenceText}>"{assembledSentence}"</Text>
                        </View>
                    ) : null}

                    {isFrontedAdverbialFirst && config.commaRuleNotice && (
                        <View style={styles.commaRuleBox}>
                            <Text style={styles.commaRuleText}>💡 {config.commaRuleNotice}</Text>
                        </View>
                    )}
                </View>

                {/* Feedback Message */}
                <View style={styles.feedbackBox}>
                    <Text style={styles.feedbackText}>{statusMessage}</Text>
                </View>

                {/* Available Clause Pool */}
                <View style={styles.poolSection}>
                    <Text style={styles.poolTitle}>Available Clause Blocks:</Text>
                    <View style={styles.poolContainer}>
                        {unselectedClauses.length === 0 ? (
                            <Text style={styles.allPlacedText}>All clauses positioned in your sentence.</Text>
                        ) : (
                            unselectedClauses.map((clause) => {
                                const theme = ROLE_COLORS[clause.role] || ROLE_COLORS.main_clause;
                                return (
                                    <TouchableOpacity
                                        key={`pool_${clause.id}`}
                                        style={[styles.clauseCard, { backgroundColor: theme.bg, borderColor: theme.border }]}
                                        onPress={() => selectClause(clause.id)}
                                        disabled={hasCompleted}
                                        accessibilityLabel={`Add clause ${clause.text}`}
                                        testID={`pool-clause-${clause.id}`}
                                    >
                                        <View style={styles.clauseCardHeader}>
                                            <Text style={[styles.clauseCardRole, { color: theme.text }]}>
                                                {theme.label}
                                            </Text>
                                        </View>
                                        <Text style={styles.clauseCardText}>{clause.text}</Text>
                                    </TouchableOpacity>
                                );
                            })
                        )}
                    </View>

                    <View style={styles.actionRow}>
                        <JuicyButton
                            label="CHECK SENTENCE"
                            variant="primary"
                            size="medium"
                            onPress={handleCheck}
                            disabled={hasCompleted || selectedIds.length < allClauses.length}
                            testID="clause-check-button"
                        />
                    </View>
                </View>
            </View>
        </StageCard>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    promptText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    sentenceStage: {
        backgroundColor: '#F8FAFC',
        padding: 14,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 10,
    },
    stageHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    stageTitle: {
        fontSize: 12,
        fontWeight: '800',
        color: '#64748B',
    },
    resetButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 2,
    },
    resetButtonText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
    },
    emptyStage: {
        paddingVertical: 24,
        alignItems: 'center',
    },
    emptyStageText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#94A3B8',
    },
    stagedTokensContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    stagedToken: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 2,
        maxWidth: '100%',
    },
    stagedTokenRole: {
        fontSize: 11,
        fontWeight: '800',
        marginBottom: 2,
    },
    stagedTokenText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#1E293B',
    },
    liveSentenceBox: {
        backgroundColor: '#FFFFFF',
        padding: 10,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#CBD5E1',
    },
    liveSentenceText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#0F172A',
        fontStyle: 'italic',
    },
    commaRuleBox: {
        backgroundColor: '#FEF3C7',
        padding: 8,
        borderRadius: 8,
    },
    commaRuleText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#B45309',
    },
    feedbackBox: {
        backgroundColor: '#F1F5F9',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
    feedbackText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        textAlign: 'center',
    },
    poolSection: {
        gap: 8,
        alignItems: 'center',
    },
    poolTitle: {
        fontSize: 13,
        fontWeight: '800',
        color: '#475569',
        alignSelf: 'flex-start',
    },
    poolContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        width: '100%',
    },
    allPlacedText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#10B981',
        paddingVertical: 8,
    },
    clauseCard: {
        flex: 1,
        minWidth: 160,
        padding: 10,
        borderRadius: 10,
        borderWidth: 2,
        gap: 4,
    },
    clauseCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    clauseCardRole: {
        fontSize: 11,
        fontWeight: '800',
    },
    clauseCardText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#1E293B',
    },
    actionRow: {
        marginTop: 6,
    },
});
